import React from 'react';
import { getClassName } from '../Grid/Grid';

type SpanProps =
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '10'
  | '11'
  | '12';
export type Span = {
  fixed?: SpanProps;
  xs?: SpanProps;
  sm?: SpanProps;
  md?: SpanProps;
  lg?: SpanProps;
  xlg?: SpanProps;
  ''?: SpanProps;
};
export type ColumnProps = {
  alignContent?:
    | 'stretch'
    | 'start'
    | 'end'
    | 'center'
    | 'space-between'
    | 'space-around';
  alignItems?: 'stretch' | 'start' | 'end' | 'center' | 'baseline';
  alignSelf?: 'stretch' | 'start' | 'end' | 'center';
  bleed?: 'x' | 'y' | 'both';
  className?: string;
  direction?: 'row' | 'column' | 'row-reverse' | 'column-reverse';
  gridCell?: boolean;
  justifyContent?:
    | 'start'
    | 'end'
    | 'center'
    | 'space-between'
    | 'space-around';
  wrap?: 'no-wrap' | 'wrap' | 'wrap-reverse';
};

type ColumnExtraProps<C extends React.ElementType> = {
  order?: string | string[];
  span?: Span;
  style?: React.CSSProperties;
  tag?: C;
};

type ColumnExtendedProps<
  C extends React.ElementType,
  ColumnProps,
> = React.PropsWithChildren<ColumnProps & ColumnExtraProps<C>> &
  React.ComponentPropsWithoutRef<C>;

/**
 * @name getColSpans
 * @function generates an array of column span sizes for
 * breakpoints passed into the column.
 *
 * @param {Object} span - span prop object.
 *
 * @returns {Array} array if size classnames.
 */
export function getColSpans(span: Span): string | string[] {
  if (span.fixed) return [`grid__col-${span.fixed}`];

  return Object.keys(span).map((size) => {
    const spanSize = span[size as keyof Span];
    return size ? `grid__col-${size}-${spanSize}` : `grid__col-${spanSize}`;
  });
}

const Column = <C extends React.ElementType = 'section'>({
  alignContent = 'stretch',
  alignItems = 'stretch',
  alignSelf,
  bleed,
  children,
  className,
  direction = 'row',
  gridCell = false,
  justifyContent = 'start',
  order,
  span,
  style,
  tag,
  wrap = 'wrap',
}: ColumnExtendedProps<C, ColumnProps>) => {
  const Tag = tag || 'section';
  const properties: ColumnProps = {
    alignContent,
    alignItems,
    alignSelf,
    bleed,
    direction,
    gridCell,
    justifyContent,
    wrap,
  };
  let orders = [];

  // Get the reflex-grid specific classname/decorator;
  const classnames = Object.keys(properties).reduce(
    (previous, current) => {
      const compClass = getClassName(
        current,
        properties[current as keyof ColumnProps]
      );
      if (compClass !== null) previous.push(compClass);
      return previous;
    },
    ['']
  );

  // Apply base column classes.
  classnames.push('grid__column');

  // Get the span sizes for breakpoints/grid;
  const sizes = span ? getColSpans(span) : ['grid__col-auto'];
  classnames.push(...sizes);

  // Get bleed and order decorators;
  if (bleed)
    classnames.push(
      bleed === 'both' ? 'grid__col--bleed' : `grid__col--bleed-${bleed}`
    );
  if (order) {
    if (typeof order === 'string') {
      orders.push(order);
    } else {
      orders = order;
    }
    orders.forEach((hodor) => {
      classnames.push(`grid--order-${hodor}`);
    });
  }

  // join all the decorators together append any supplied className prop
  if (className) classnames.push(className);
  const colClassName = classnames.join(' ');

  return (
    <Tag className={`${colClassName}`} style={style} data-grid="column">
      {gridCell && <div className="grid__cell">{children}</div>}
      {!gridCell && children}
    </Tag>
  );
};

Column.displayName = 'Column';

export default Column;
